import { useImperativeHandle, useRef, useState, type RefObject } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@blueskydataplatform/shared-ui/components/ui/alert-dialog";

import Logo from "@/components/logo";
import useQueryParams from "@/hooks/use-query-params";
import { useMcpProvider } from "@/providers/mcp-providers-context";
import McpAccessDestination from "./mcp-access-destination";
import { GENERIC_CLIENT, useMcpClientName } from "./mcp-client-name";

export type McpCallbackHandle = {
  /** Resolves true once the visitor allows the connection, false if they cancel. */
  askConsent: () => Promise<boolean>;
  /** Hands the software statement to mcp-auth-server; navigates away. */
  submit: (softwareStatement: string) => void;
};

/**
 * The hidden form that hands the software statement back to mcp-auth-server, and the consent
 * dialog in front of it. Four screens complete the same handshake, so they share this rather
 * than four copies of it.
 *
 * The consent step is H1 4071477: anyone can register an MCP client, so a crafted link to
 * our login page used to connect the attacker's client to whatever org the visitor was signed
 * in to, without a click. The finish-registration screens skip it: their own button is worded
 * as the Allow and shows the same facts.
 *
 * Exposes no handle when the link carries no `callback_url`. A form with an empty action
 * posts to the current page, which is how a malformed link used to look like the button
 * simply doing nothing; with no handle, completeMcpAuthCallback fails and says so.
 */
const McpCallbackForm = ({ handleRef }: { handleRef: RefObject<McpCallbackHandle | null> }) => {
  const { callbackUrl, state } = useQueryParams();
  const { provider } = useMcpProvider();
  const clientName = useMcpClientName(state, callbackUrl) ?? GENERIC_CLIENT;
  const formRef = useRef<HTMLFormElement>(null);
  const softwareStatementInputRef = useRef<HTMLInputElement>(null);
  const [answer, setAnswer] = useState<((allowed: boolean) => void) | null>(null);

  useImperativeHandle<McpCallbackHandle | null, McpCallbackHandle | null>(
    handleRef,
    () =>
      callbackUrl
        ? {
            askConsent: () => new Promise<boolean>(resolve => setAnswer(() => resolve)),
            submit: softwareStatement => {
              softwareStatementInputRef.current!.value = softwareStatement;
              formRef.current!.submit();
            },
          }
        : null,
    [callbackUrl]
  );

  if (!callbackUrl) {
    return null;
  }

  const decide = (allowed: boolean) => {
    answer?.(allowed);
    setAnswer(null);
  };

  return (
    <>
      <form action={callbackUrl} ref={formRef} method="POST">
        <input type="hidden" name="state" value={state || ""} />
        <input type="hidden" name="software_statement" ref={softwareStatementInputRef} />
      </form>
      <AlertDialog open={answer !== null} onOpenChange={open => !open && decide(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex justify-center">
              <Logo className="mb-4" />
            </div>
            <AlertDialogTitle className="text-center">
              Allow {clientName} to access your {provider?.name ?? "account"} data?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              {clientName} will be able to read your organization’s data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <McpAccessDestination className="text-center" />
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => decide(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => decide(true)}>Allow</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default McpCallbackForm;
